import { computed, ref } from "vue"

export interface UseProgressAnimationOptions {
  /** Initial progress value (0-100) */
  initialProgress?: number
  /** Animation speed in milliseconds */
  animationSpeed?: number
  /** Delay in milliseconds before calling onComplete */
  completionDelay?: number
  /** Callback function to execute when progress reaches 100% */
  onComplete?: () => void
}

export function useProgressAnimation(options: UseProgressAnimationOptions = {}) {
  const { initialProgress = 0, animationSpeed = 100, completionDelay = 1000, onComplete } = options

  const progress = ref(initialProgress)
  let progressInterval: number | undefined = undefined

  /**
   * Start progress animation that follows a natural curve
   * - Starts slow (0-30%)
   * - Accelerates in the middle (30-70%)
   * - Slows down at the end (70-100%)
   */
  function startProgressAnimation() {
    // Clear any existing interval
    clearProgressAnimation()

    // Reset progress
    progress.value = initialProgress

    // Create a simulation of progress that follows a natural curve
    progressInterval = setInterval(() => {
      if (progress.value < 30) {
        // Initial phase - slower progress
        progress.value += 1 + Math.random() * 2
      } else if (progress.value < 70) {
        // Middle phase - faster progress
        progress.value += 2 + Math.random() * 3
      } else if (progress.value < 100) {
        // Late phase - slower progress
        progress.value += 0.5 + Math.random() * 1
      }

      // If we reach 100%, clear the interval
      if (progress.value >= 100) {
        completeProgress()
      }
    }, animationSpeed) as unknown as number // Cast to number as browser returns number
  }

  /**
   * Clear the progress animation interval
   */
  function clearProgressAnimation() {
    if (progressInterval !== undefined) {
      clearInterval(progressInterval)
      progressInterval = undefined
    }
  }

  /**
   * Complete the progress animation with success
   * - Sets progress to 100%
   * - Clears animation interval
   * - Calls onComplete callback after delay
   */
  function completeProgress(options: { shouldCallOnComplete: boolean } = { shouldCallOnComplete: true }) {
    clearProgressAnimation()
    progress.value = 100

    if (onComplete && options.shouldCallOnComplete) {
      setTimeout(() => {
        onComplete()
      }, completionDelay)
    }
  }

  /**
   * Reset the progress animation
   */
  function resetProgress() {
    clearProgressAnimation()
    progress.value = initialProgress
  }

  return {
    progress: computed(() => progress.value),
    startProgressAnimation,
    clearProgressAnimation,
    completeProgress,
    resetProgress,
  }
}
